import { type CSSProperties, type ReactNode } from "react";
import { extendedColors, sysColors } from "@/assets/theme/colors";
import type { MaterialNames } from "@/components/material_symbol_names";

export type BannerVariant = "info" | "warn" | "error";
export type BannerLayout = "contained" | "full" | "header";

export const variantSurfaceClassNames: Record<BannerVariant, string> = {
  info: "bg-sys-brand-primary-container-bright text-sys-brand-on-surface",
  warn: "text-sys-brand-on-surface",
  error: "text-sys-brand-on-surface"
};

export const variantChipClassNames: Record<BannerVariant, string> = {
  info: "bg-sys-brand-primary",
  warn: "bg-extended-warning-brand-color",
  error: "bg-sys-brand-error"
};

export const variantIcons: Record<BannerVariant, MaterialNames> = {
  info: "info",
  warn: "warning",
  error: "error"
};

export const variantIconClassNames: Record<BannerVariant, string> = {
  info: "text-sys-brand-on-primary",
  warn: "text-extended-warning-brand-on-color",
  error: "text-sys-brand-on-error"
};

export const variantAccentColors: Record<BannerVariant, string> = {
  info: sysColors["sys-brand-primary"],
  warn: extendedColors["extended-warning-brand-color"],
  error: sysColors["sys-brand-error"]
};

const variantOnColors: Record<BannerVariant, string> = {
  info: sysColors["sys-brand-on-primary"],
  warn: extendedColors["extended-warning-brand-on-color"],
  error: sysColors["sys-brand-on-error"]
};

export function bannerPrimaryButtonStyle(variant: BannerVariant): CSSProperties {
  const actionColor = variantAccentColors[variant];
  const onColor = variantOnColors[variant];
  return {
    "--md-sys-color-primary": actionColor,
    "--md-sys-color-on-primary": onColor,
    "--md-focus-ring-color": actionColor,
    "--md-filled-button-focus-outline-color": actionColor
  } as CSSProperties;
}

export interface Action {
  label: string;
  /** Shorter label shown below the `sm` breakpoint. */
  compactLabel?: string;
  link?: string;
  isInternal?: boolean;
  isDismiss?: boolean;
  onClick?: () => void;
}

export interface BannerProps {
  description: string;
  /** Shorter description shown below the `sm` breakpoint. */
  compactDescription?: string;
  variant?: BannerVariant;
  secondaryAction?: Action;
  primaryAction?: Action;
  closable?: boolean;
  onClose?: () => void;
  className?: string;
  /**
   * Width treatment. `contained` fills the parent. `full` is edge-to-edge
   * (dev environment). `header` matches the compact flush TopActionBar
   * (assume user) with compact inner padding.
   */
  layout?: BannerLayout;
  /** Keep actions in one row while the message wraps. Independent of `layout`. */
  inline?: boolean;
  subText?: ReactNode | ReactNode[];
  testId?: string;
  hideSecondaryDismiss?: boolean;
}
